import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNativeKeyboard } from '../contexts/NativeKeyboardContext';

export interface UseKeyboardAwareOptions {
  /** Extra padding to add above keyboard (default: 16) */
  extraPadding?: number;
  /** Whether to auto-scroll focused element into view (default: true) */
  autoScrollOnFocus?: boolean;
  /** Scroll behavior: 'smooth' or 'auto' (default: 'smooth') */
  scrollBehavior?: ScrollBehavior;
  /** Block position for scrollIntoView (default: 'center') */
  scrollBlock?: ScrollLogicalPosition;
}

export interface UseKeyboardAwareReturn {
  /** Whether keyboard is currently visible */
  isKeyboardOpen: boolean;
  /** Keyboard height in pixels (0 when hidden) */
  keyboardHeight: number;
  /** Bottom padding to apply to content when keyboard is open */
  bottomPadding: number;
  /** Ref to attach to the scroll container */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  /** Handler to scroll an element into view - call on TextField focus */
  scrollIntoViewOnFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Check if this is a native platform (iOS/Android) */
  isNativePlatform: boolean;
}

/**
 * Hook for making bottom sheets and dialogs keyboard-aware on Capacitor (iOS/Android).
 *
 * On iOS with KeyboardResize.None, the keyboard overlays the webview without resizing it,
 * so we need to manually adjust padding and scroll focused inputs into view.
 *
 * Usage:
 * ```tsx
 * const { isKeyboardOpen, bottomPadding, scrollIntoViewOnFocus, scrollContainerRef } = useKeyboardAware();
 *
 * return (
 *   <Box ref={scrollContainerRef} sx={{ overflowY: 'auto', pb: bottomPadding }}>
 *     <TextField onFocus={scrollIntoViewOnFocus} />
 *   </Box>
 * );
 * ```
 */
export function useKeyboardAware(options: UseKeyboardAwareOptions = {}): UseKeyboardAwareReturn {
  const {
    extraPadding = 16,
    autoScrollOnFocus = true,
    scrollBehavior = 'smooth',
    scrollBlock = 'center',
  } = options;

  const { keyboardInsetPx } = useNativeKeyboard();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNativePlatform = Capacitor.isNativePlatform();
  const isIOS = Capacitor.getPlatform() === 'ios';

  // For web, use visualViewport API as fallback
  const [webKeyboardHeight, setWebKeyboardHeight] = useState(0);

  useEffect(() => {
    if (isNativePlatform) return;

    const handleResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;
        // Only consider it a keyboard if the height difference is significant (> 100px)
        setWebKeyboardHeight(keyboardHeight > 100 ? keyboardHeight : 0);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }
  }, [isNativePlatform]);

  // Use native keyboard height on Capacitor, web detection on browser
  const keyboardHeight = isNativePlatform ? keyboardInsetPx : webKeyboardHeight;
  const isKeyboardOpen = keyboardHeight > 0;

  // Calculate bottom padding - on iOS we need the full keyboard height since layout doesn't resize
  // On Android, the webview resizes above the keyboard, so we only need extra padding
  const bottomPadding = isKeyboardOpen
    ? (isIOS ? keyboardHeight + extraPadding : extraPadding)
    : 0;

  // Scroll focused element into view with a delay to let keyboard animation complete.
  // On Capacitor, element.scrollIntoView often targets the wrong scroller; when scrollContainerRef
  // is attached to the sheet's overflow region, scroll that container explicitly using keyboard overlap.
  const scrollIntoViewOnFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!autoScrollOnFocus) return;

      const element = event.target;

      // Delay to allow keyboard to fully appear
      const delay = isNativePlatform ? 250 : 100;

      setTimeout(() => {
        if (document.activeElement !== element) return;

        const container = scrollContainerRef.current;
        const keyboardOverlap =
          keyboardHeight > 0 ? keyboardHeight + extraPadding : 0;
        const viewportBottom = window.innerHeight - keyboardOverlap;

        if (container && container.contains(element)) {
          const elRect = element.getBoundingClientRect();
          const cr = container.getBoundingClientRect();
          const bottomLimit = Math.min(cr.bottom, viewportBottom);
          const topLimit = cr.top + extraPadding;

          if (elRect.bottom > bottomLimit) {
            container.scrollTop += elRect.bottom - bottomLimit + 8;
          } else if (elRect.top < topLimit) {
            container.scrollTop += elRect.top - topLimit - 8;
          }
          return;
        }

        element.scrollIntoView({
          behavior: scrollBehavior,
          block: scrollBlock,
        });
      }, delay);
    },
    [
      autoScrollOnFocus,
      scrollBehavior,
      scrollBlock,
      isNativePlatform,
      extraPadding,
      keyboardHeight,
    ]
  );

  return {
    isKeyboardOpen,
    keyboardHeight,
    bottomPadding,
    scrollContainerRef,
    scrollIntoViewOnFocus,
    isNativePlatform,
  };
}

export default useKeyboardAware;
